import { generateFinalScript } from '../../../store/editorUtils';

/**
 * Check if the file is of an accepted type
 * @param file - The file to check
 * @param accept - The accepted file types
 * @returns True if the file is of an accepted type, false otherwise
 * @note We are favoring a simpler api that only looks at the file extension
 * the reason being we can reuse the accept string vs having to also have a mapping
 * of mime types to extensions
 */
const isAcceptedFileType = (file: File, accept: string) => {
  const acceptedTypes = accept.split(',');
  const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
  return acceptedTypes.some(type => {
    return fileExtension === type.toLowerCase();
  });
};

const isSizeLimitExceeded = (file: File, sizeLimit: string) => {
  const sizeLimitUpper = sizeLimit.toUpperCase();

  if (sizeLimitUpper.endsWith('KB')) {
    const sizeLimitBytes = parseInt(sizeLimitUpper.split('KB')[0], 10) * 1024;
    return file.size > sizeLimitBytes;
  }
  if (sizeLimitUpper.endsWith('MB')) {
    const sizeLimitBytes =
      parseInt(sizeLimitUpper.split('MB')[0], 10) * 1024 * 1024;
    return file.size > sizeLimitBytes;
  }
  if (sizeLimitUpper.endsWith('GB')) {
    const sizeLimitBytes =
      parseInt(sizeLimitUpper.split('GB')[0], 10) * 1024 * 1024 * 1024;
    return file.size > sizeLimitBytes;
  }
  throw new Error('Size limit must be in KB, MB, or GB');
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
};

/**
 * @description Checks if the url is valid throwing an exception from the URL constructor
 * if it is invalid
 * @see https://developer.mozilla.org/en-US/docs/Web/API/URL/URL
 */
const isValidUrl = (url: string) => {
  try {
    if (!url.startsWith('https')) {
      return false;
    }
    // eslint-disable-next-line no-new
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex > 0 ? filename.slice(lastDotIndex + 1).toLowerCase() : '';
};

const splitScriptByRenderMode = (
  text: string,
  limits: {
    character: number;
    paragraph: number;
    sentence: number;
  },
  preferredMode: Parameters<typeof generateFinalScript>[0]
) => {
  const result = generateFinalScript(preferredMode, { text, textFormat: '' });

  const limitMap: Record<typeof preferredMode, number> = {
    all: limits.character,
    paragraph: limits.paragraph,
    sentence: limits.sentence,
  };

  const limit = limitMap[preferredMode];

  if (preferredMode === 'all') {
    return {
      result: [result.scriptArrayToRender[0].slice(0, limit)],
      renderMode: preferredMode,
    };
  }

  return {
    result: result.scriptArrayToRender.slice(0, limit),
    renderMode: preferredMode,
  };
};

export {
  getFileExtension,
  isAcceptedFileType,
  isSizeLimitExceeded,
  formatFileSize,
  isValidUrl,
  splitScriptByRenderMode,
};
